<template>
    <!-- Modal exclusivo de facturación de extras SIN contrato -->
    <modal
        name="gm-bill-extra-without-contract"
        :width="'92%'"
        :height="'auto'"
        :adaptive="true"
        :scrollable="true"
        :click-to-close="false"
    >
        <div class="billing-modal billing-modal--without-contract">
            <div class="billing-modal__header">
                <div class="billing-modal__title-group">
                    <div class="billing-modal__icon"><i class="fas fa-file-invoice-dollar"></i></div>
                    <div>
                        <h4 class="billing-modal__title">Facturar extra sin contrato</h4>
                        <p class="billing-modal__subtitle mb-0">
                            El cargo precarga los conceptos, pero puedes ajustar cantidades, precios, clasificación SAT y reglas fiscales del CFDI.
                        </p>
                    </div>
                </div>
                <button type="button" class="billing-modal__close" title="Cerrar" aria-label="Cerrar"
                    :disabled="send" @click="closedModal">
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <div v-if="load" class="billing-loading">
                <div class="billing-loading__spinner"><i class="fas fa-circle-notch fa-spin"></i></div>
                <h5>Preparando información</h5>
                <p class="text-muted mb-0">Cargando el cargo, los conceptos y los datos fiscales del cliente.</p>
            </div>

            <template v-else>
                <div class="billing-modal__source">
                    <div class="billing-modal__source-data">
                        <span class="billing-modal__source-label">CARGO EXTRAORDINARIO #{{ source.id || '—' }}</span>
                        <strong>{{ userName }}</strong>
                        <small>{{ companyName }} <span v-if="source.due_date">· {{ formatDate(source.due_date) }}</span></small>
                    </div>
                    <div class="billing-modal__source-meta">
                        <span class="billing-modal__source-badge">Sin contrato</span>
                        <span>Moneda original: <b>{{ currencyLabel(sourceCurrencyId) }}</b></span>
                        <span v-if="source.is_paid !== undefined">{{ isPaid ? 'Pagado' : 'Pendiente de pago' }}</span>
                    </div>
                </div>

                <div v-if="contextError" class="billing-modal__message billing-modal__message--danger" role="alert">
                    <i class="fas fa-exclamation-circle mr-2"></i>{{ contextError }}
                </div>
                <div v-if="formError" class="billing-modal__message billing-modal__message--danger" role="alert">
                    <i class="fas fa-exclamation-triangle mr-2"></i>{{ formError }}
                </div>
                <div v-else-if="contextReady && validationError"
                    class="billing-modal__message billing-modal__message--warning" role="status">
                    <i class="fas fa-info-circle mr-2"></i>{{ validationError }}
                </div>

                <div class="billing-modal__body">
                    <div class="row">
                        <div class="col-lg-4 mb-4 mb-lg-0">
                            <div class="billing-section-header">
                                <div class="billing-section-header__left">
                                    <div class="billing-section-header__number">1</div>
                                    <div>
                                        <h5 class="billing-section-header__title">Configuración de facturas</h5>
                                        <p class="billing-section-header__text">Receptor, moneda y condiciones de emisión.</p>
                                    </div>
                                </div>
                                <span class="billing-count-badge">{{ billsData.length }}</span>
                            </div>

                            <div class="invoice-list">
                                <div class="invoice-card" v-for="(bill, index) in billsData" :key="bill.local_key">
                                    <div class="invoice-card__header">
                                        <div class="invoice-card__number">{{ index + 1 }}</div>
                                        <div class="invoice-card__heading">
                                            <strong>{{ bill.name || ('Factura ' + (index + 1)) }}</strong>
                                            <small>{{ Number(bill.type) === 1 ? 'Facturación integrada' : 'Facturación desglosada' }}</small>
                                        </div>
                                        <button v-if="billsData.length > 1" class="billing-modal__remove"
                                            type="button" :disabled="send" @click="removeBillData(index)" title="Quitar factura">
                                            <i class="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                    <div class="invoice-card__body">
                                        <div class="form-group">
                                            <label class="billing-label">Nombre de la factura</label>
                                            <input type="text" v-model.trim="bill.name" maxlength="120"
                                                class="form-control billing-control" :disabled="send"
                                                placeholder="Ej. Factura principal">
                                        </div>
                                        <div class="form-group">
                                            <label class="billing-label">Datos de facturación</label>
                                            <select v-model.number="bill.billing_data_id" class="form-control billing-control" :disabled="send">
                                                <option :value="null" disabled>Selecciona los datos fiscales</option>
                                                <option v-for="billingData in billingDataOptions" :key="billingData.id"
                                                    :value="Number(billingData.id)">
                                                    {{ billingData.business_name || billingData.name || 'Datos fiscales' }} - {{ billingData.rfc }}
                                                </option>
                                            </select>
                                            <small v-if="!billingDataOptions.length" class="billing-modal__help-warning">
                                                No hay datos fiscales disponibles para este cliente.
                                            </small>
                                        </div>
                                        <div class="form-group">
                                            <label class="billing-label">Tipo de facturación</label>
                                            <select v-model.number="bill.type" class="form-control billing-control" :disabled="send">
                                                <option :value="1">Integrado</option>
                                                <option :value="2">Desglosado</option>
                                            </select>
                                            <small class="billing-help" v-if="Number(bill.type) === 1">
                                                Los conceptos integrados deben compartir clave SAT, unidad e impuestos.
                                            </small>
                                            <small class="billing-help" v-else>
                                                Cada concepto tiene su propia cantidad, clave SAT e impuestos configurables.
                                            </small>
                                        </div>
                                        <div class="form-group" v-if="isUsdCharge()">
                                            <label class="billing-label">Moneda de la factura</label>
                                            <select v-model.number="bill.currency_id" @change="onInvoiceCurrencyChange(bill)"
                                                class="form-control billing-control" :disabled="send">
                                                <option v-for="currency in invoiceCurrencies" :key="currency.id"
                                                    :value="Number(currency.id)">{{ currency.name }}</option>
                                            </select>
                                            <small class="billing-help">El cargo está en USD: puedes emitir en USD o convertir a MXN.</small>
                                        </div>
                                        <div class="form-group" v-else>
                                            <label class="billing-label">Moneda de la factura</label>
                                            <div class="billing-readonly-value">MXN · Pesos mexicanos</div>
                                        </div>
                                        <div class="form-group exchange-panel" v-if="requiresExchange(bill)">
                                            <div class="exchange-panel__title"><i class="fas fa-exchange-alt"></i>
                                                {{ requiresCurrencyConversion(bill) ? 'Conversión USD → MXN' : 'Tipo de cambio USD' }}
                                            </div>
                                            <label class="billing-label">Tipo de cambio (MXN por 1 USD)</label>
                                            <money class="form-control billing-control" v-model="bill.exchange_rate_bill"
                                                :disabled="send"></money>
                                            <small class="billing-help" v-if="requiresCurrencyConversion(bill)">
                                                Multiplica los conceptos USD por el tipo de cambio para expresar el CFDI en MXN.
                                            </small>
                                            <small class="billing-help" v-else>
                                                El CFDI permanece en USD; el tipo de cambio se informa sin cambiar los importes.
                                            </small>
                                        </div>
                                        <div class="form-group">
                                            <label class="billing-label">Método de pago</label>
                                            <select v-model="bill.payment_method" @change="onPaymentMethodChange(bill)"
                                                class="form-control billing-control" :disabled="send">
                                                <option value="PUE">PUE | Pago en una sola exhibición</option>
                                                <option value="PPD">PPD | Pago en parcialidades o diferido</option>
                                            </select>
                                        </div>
                                        <div class="form-group mb-0">
                                            <label class="billing-label">Forma de pago</label>
                                            <select v-model="bill.payment_form" class="form-control billing-control"
                                                :disabled="send || bill.payment_method === 'PPD'">
                                                <option value="" disabled>Seleccionar</option>
                                                <option value="01">01 | Efectivo</option>
                                                <option value="02">02 | Cheque</option>
                                                <option value="03">03 | Transferencia electrónica</option>
                                                <option value="04">04 | Tarjeta de crédito</option>
                                                <option value="28">28 | Tarjeta de débito</option>
                                                <option value="99" v-if="bill.payment_method === 'PPD'">99 | Por definir</option>
                                            </select>
                                        </div>
                                        <div class="billing-modal__invoice-total" v-if="billHasItems(index)">
                                            <span>Total estimado</span>
                                            <strong>{{ invoiceTotalFormatted(index) }}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button type="button" class="btn billing-btn billing-btn--add w-100"
                                :disabled="send || !contextReady" @click="addBillData()">
                                <i class="fas fa-plus mr-2"></i> Agregar factura
                            </button>
                        </div>

                        <div class="col-lg-8">
                            <div class="billing-section-header billing-section-header--items">
                                <div class="billing-section-header__left">
                                    <div class="billing-section-header__number">2</div>
                                    <div>
                                        <h5 class="billing-section-header__title">Conceptos a facturar</h5>
                                        <p class="billing-section-header__text">
                                            Los conceptos se cargan automáticamente; ajusta aquí la configuración fiscal sin cambiar el adeudo original.
                                        </p>
                                    </div>
                                </div>
                                <span class="billing-count-badge">{{ bills.length }}</span>
                            </div>
                            <div v-if="!bills.length" class="billing-empty-state">
                                <div class="billing-empty-state__icon"><i class="fas fa-receipt"></i></div>
                                <h5>No hay conceptos pendientes</h5>
                                <p>Este cargo no tiene conceptos disponibles para facturar.</p>
                            </div>

                            <div class="item-card" v-for="(item, index) in bills" :key="item.id">
                                <div class="item-card__header">
                                    <div class="item-card__title">
                                        <span class="item-card__index">{{ index + 1 }}</span>
                                        <div>
                                            <span class="item-card__label">CONCEPTO REGISTRADO</span>
                                            <strong>{{ item.original_description }}</strong>
                                        </div>
                                    </div>
                                    <div class="item-card__amount">
                                        <span>Total del cargo</span>
                                        <strong>{{ money(item.original_final_amount, sourceCurrencyId) }}</strong>
                                    </div>
                                </div>
                                <div class="item-card__body">
                                    <div class="form-group">
                                        <label class="billing-label">Descripción que aparecerá en el CFDI</label>
                                        <input v-model.trim="item.description" type="text" maxlength="1000"
                                            class="form-control billing-control" :disabled="send"
                                            placeholder="Descripción fiscal del producto o servicio">
                                        <small class="billing-help">Puedes precisar la descripción fiscal sin alterar la descripción original del cargo.</small>
                                    </div>
                                    <div class="row">
                                        <div class="col-md-5">
                                            <div class="form-group">
                                                <label class="billing-label">Asignar a factura</label>
                                                <select v-model.number="item.bill_id" class="form-control billing-control" :disabled="send">
                                                    <option v-for="(bill, billIndex) in billsData" :key="bill.local_key" :value="billIndex">
                                                        {{ bill.name || ('Factura ' + (billIndex + 1)) }}
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                        <div class="col-md-3">
                                            <div class="form-group">
                                                <label class="billing-label">Cantidad CFDI</label>
                                                <input type="number" step="0.000001" min="0.000001" v-model.number="item.quantity"
                                                    class="form-control billing-control" :disabled="send" @input="invalidateConfirmation">
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <div class="form-group">
                                                <label class="billing-label">Precio unitario ({{ currencyLabel(sourceCurrencyId) }})</label>
                                                <input type="number" step="0.000001" min="0.000001" v-model.number="item.unit_price"
                                                    class="form-control billing-control" :disabled="send" @input="invalidateConfirmation">
                                                <small class="billing-help">Subtotal bruto: {{ money(item.quantity * item.unit_price, sourceCurrencyId) }}</small>
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <div class="form-group">
                                                <label class="billing-label">Clave producto/servicio SAT</label>
                                                <input v-model.trim="item.product_key" maxlength="8" inputmode="numeric"
                                                    class="form-control billing-control" placeholder="8 dígitos" :disabled="send">
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <div class="form-group">
                                                <label class="billing-label">Unidad SAT</label>
                                                <input v-model.trim="item.unit_key" maxlength="3"
                                                    class="form-control billing-control" placeholder="H87 / E48" :disabled="send"
                                                    @input="onUnitKeyChange(item)">
                                                <small class="billing-help">Ejemplos: H87 · Pieza / E48 · Unidad de servicio. Verifica la unidad aplicable.</small>
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <div class="form-group">
                                                <label class="billing-label">Descuento total ({{ currencyLabel(sourceCurrencyId) }})</label>
                                                <input type="number" step="0.01" min="0" v-model.number="item.discount"
                                                    class="form-control billing-control" :disabled="send" @input="invalidateConfirmation">
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <div class="form-group">
                                                <label class="billing-label">Nombre de unidad</label>
                                                <input v-model.trim="item.unit_name" maxlength="50" :disabled="send"
                                                    class="form-control billing-control" placeholder="Pieza / Servicio / Hora">
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <div class="form-group">
                                                <label class="billing-label">Objeto de impuesto SAT</label>
                                                <select v-model="item.taxability" class="form-control billing-control"
                                                    :disabled="send" @change="invalidateConfirmation">
                                                    <option value="" disabled>Selecciona clasificación</option>
                                                    <option value="01">01 · No objeto de impuesto</option>
                                                    <option value="02">02 · Sí objeto de impuesto</option>
                                                    <option value="03">03 · Objeto, no obligado a desglose</option>
                                                    <option value="04">04 · Objeto, no causa impuesto</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="conversion-preview" v-if="requiresCurrencyConversion(billsData[item.bill_id])">
                                        <div class="conversion-preview__icon"><i class="fas fa-calculator"></i></div>
                                        <div class="conversion-preview__content">
                                            <strong>Conversión del concepto</strong>
                                            <span v-if="hasValidExchangeRate(billsData[item.bill_id])">
                                                {{ money(item.quantity * item.unit_price, sourceCurrencyId) }} USD ×
                                                {{ billsData[item.bill_id].exchange_rate_bill }} =
                                                <b>{{ money(getConvertedAmount(item), 1) }} MXN</b>
                                            </span>
                                            <span v-else class="conversion-preview__pending">
                                                Captura el tipo de cambio para visualizar el equivalente en MXN.
                                            </span>
                                        </div>
                                    </div>

                                    <div class="tax-panel">
                                        <div class="tax-panel__header">
                                            <strong>Impuestos y retenciones</strong>
                                            <small>Precargados del adeudo. Puedes modificar los impuestos para este CFDI sin cambiar el cargo original.</small>
                                        </div>
                                        <div class="billing-modal__taxes">
                                            <button type="button" class="billing-modal__tax" v-for="tax in taxOptions" :key="tax.key"
                                                :disabled="send" :aria-pressed="item[tax.key] === true ? 'true' : 'false'"
                                                :class="item[tax.key] === null ? 'billing-modal__tax--unknown' : (item[tax.key] ? 'billing-modal__tax--active' : 'billing-modal__tax--inactive')"
                                                @click="toggleTax(item, tax.key)">
                                                <span class="billing-modal__tax-icon"><i :class="item[tax.key] === null ? 'fas fa-question' : (item[tax.key] ? 'fas fa-check' : 'fas fa-minus')"></i></span>
                                                <span><strong>{{ tax.label }}</strong>
                                                    <small>{{ item[tax.key] === null ? 'Por definir' : (item[tax.key] ? money(taxAmount(item, tax.key), sourceCurrencyId) : 'No aplica') }}</small>
                                                </span>
                                            </button>
                                        </div>
                                        <div v-if="!taxConfigurationComplete(item)" class="billing-modal__message billing-modal__message--warning billing-modal__message--nested">
                                            Define todos los impuestos marcados como «Por definir» antes de facturar.
                                        </div>
                                    </div>

                                    <div class="item-summary">
                                        <div class="item-summary__title">
                                            <i class="fas fa-calculator mr-2"></i> Resumen del concepto
                                            <span class="item-summary__currency">{{ getItemInvoiceCurrency(item) }}</span>
                                        </div>
                                        <div v-if="!hasValidExchangeRate(billsData[item.bill_id])"
                                            class="item-summary__warning">Captura el tipo de cambio para calcular el equivalente en MXN.</div>
                                        <template v-else>
                                            <div class="item-summary__row"><span>Importe base</span>
                                                <strong>{{ money(getConvertedAmount(item), billsData[item.bill_id].currency_id) }}</strong>
                                            </div>
                                            <div class="item-summary__row" v-if="item.discount > 0"><span>Descuento</span>
                                                <strong>− {{ money(item.discount * conversionFactor(billsData[item.bill_id]), billsData[item.bill_id].currency_id) }}</strong>
                                            </div>
                                            <div class="item-summary__row item-summary__row--subtotal"><span>Subtotal</span>
                                                <strong>{{ money(calcularTotales(item).subtotal * conversionFactor(billsData[item.bill_id]), billsData[item.bill_id].currency_id) }}</strong>
                                            </div>
                                            <div class="item-summary__row" v-if="item.ieps"><span>IEPS 8%</span>
                                                <strong>+ {{ money(calcularTotales(item).ieps * conversionFactor(billsData[item.bill_id]), billsData[item.bill_id].currency_id) }}</strong>
                                            </div>
                                            <div class="item-summary__row" v-if="item.iva"><span>IVA 16%</span>
                                                <strong>+ {{ money(calcularTotales(item).iva * conversionFactor(billsData[item.bill_id]), billsData[item.bill_id].currency_id) }}</strong>
                                            </div>
                                            <div class="item-summary__row item-summary__row--retention" v-if="item.isr_ret"><span>ISR retenido 10%</span>
                                                <strong>− {{ money(calcularTotales(item).isrRet * conversionFactor(billsData[item.bill_id]), billsData[item.bill_id].currency_id) }}</strong>
                                            </div>
                                            <div class="item-summary__row item-summary__row--retention" v-if="item.iva_ret"><span>IVA retenido 10.6667%</span>
                                                <strong>− {{ money(calcularTotales(item).ivaRet * conversionFactor(billsData[item.bill_id]), billsData[item.bill_id].currency_id) }}</strong>
                                            </div>
                                            <div class="item-summary__total"><span>Total CFDI</span>
                                                <strong>{{ money(calcularTotales(item).total * conversionFactor(billsData[item.bill_id]), billsData[item.bill_id].currency_id) }}</strong>
                                            </div>
                                        </template>
                                    </div>
                                    <div v-if="!itemMatchesCharge(item) && taxConfigurationComplete(item)" class="billing-modal__message billing-modal__message--warning billing-modal__message--nested">
                                        El total fiscal calculado no coincide con el importe registrado.
                                        Diferencia: {{ money(Math.abs(calcularTotales(item).total - item.original_final_amount), sourceCurrencyId) }}.
                                    </div>
                                </div>
                            </div>

                            <div v-if="bills.length" class="billing-modal__reconcile"
                                :class="{ 'billing-modal__reconcile--error': differenceAmount > 0.01 }">
                                <strong>Conciliación ({{ currencyLabel(sourceCurrencyId) }})</strong>
                                <div><span>Total registrado de conceptos pendientes</span><b>{{ money(originalSelectedTotal, sourceCurrencyId) }}</b></div>
                                <div><span>Total fiscal calculado</span><b>{{ money(selectedTotal, sourceCurrencyId) }}</b></div>
                                <div><span>Diferencia</span><b>{{ money(differenceAmount, sourceCurrencyId) }}</b></div>
                                <div v-if="differenceAmount > 0.01" class="billing-modal__difference-confirm">
                                    <p>El CFDI tendrá un importe diferente del cargo. Esto no modifica el adeudo ni sus pagos registrados.</p>
                                    <button type="button" class="btn billing-btn billing-btn--secondary" :disabled="send"
                                        @click="differenceConfirmed = !differenceConfirmed">
                                        <i :class="differenceConfirmed ? 'fas fa-check-circle' : 'far fa-circle'" class="mr-2"></i>
                                        {{ differenceConfirmed ? 'Diferencia confirmada' : 'Confirmar importe distinto' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="billing-modal__footer">
                    <div class="billing-modal__footer-info">
                        <i class="fas fa-info-circle mr-2"></i>
                        Las modificaciones afectan solo el CFDI. El adeudo original y sus pagos se conservan sin cambios.
                    </div>
                    <div class="billing-modal__actions">
                        <button type="button" class="btn billing-btn billing-btn--secondary" :disabled="send"
                            @click="closedModal"><i class="fas fa-arrow-left mr-2"></i> Regresar</button>
                        <button type="button" class="btn billing-btn billing-btn--primary"
                            :disabled="send || load || !canSubmit" @click="triggerSave">
                            <span v-if="send"><i class="fas fa-circle-notch fa-spin mr-2"></i> Facturando...</span>
                            <span v-else><i class="fas fa-file-invoice-dollar mr-2"></i> Facturar</span>
                        </button>
                    </div>
                </div>
            </template>
        </div>
    </modal>
</template>

<script>
import { mapActions } from 'vuex';
import request from '@/services/request';
import Catalogue from '@/services/catalogue';

// Este modal es EXCLUSIVO para extras sin contrato.
// Backend requerido:
// GET  /extra_dues/{id}/invoice-context
// POST /extra_dues/{id}/invoice
// currency_id: 1 = MXN, 2 = USD.
// Backend: devolver currency_id y banderas fiscales PERSISTIDAS de cada extra_due_item.
// El modal toma las reglas guardadas como valores iniciales; la factura puede ajustarse.
// Las modificaciones son exclusivamente del CFDI y no actualizan extra_dues ni los pagos.
// Los datos fiscales, conceptos, tasas y moneda se validan nuevamente en Laravel.
// ATENCIÓN: el controlador actual de contratos (/external_bill) NO se modifica.
const VALID_CURRENCIES = [1, 2];
const TAX_KEYS = ['iva', 'ieps', 'isr_ret', 'iva_ret'];
const moneyRound = value => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
const numberOrZero = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const isSelectedTax = value => value === true || value === 1 || value === '1' || value === 'true';
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
const TAX_ALIASES = {
  iva: ['iva', 'apply_iva'],
  ieps: ['ieps', 'apply_ieps'],
  isr_ret: ['isr_ret', 'apply_isr_ret'],
  iva_ret: ['iva_ret', 'apply_iva_ret'],
};
// No inferir una bandera fiscal restando base_amount de final_amount:
// esa diferencia puede incluir impuestos, descuentos u otros ajustes.
function storedTax(item, key) {
  const sources = [item, item.tax_rules, item.taxes, item.tax_configuration, item.tax_breakdown]
    .filter(value => value && typeof value === 'object' && !Array.isArray(value));
  for (const source of sources) {
    for (const alias of TAX_ALIASES[key]) {
      if (hasOwn(source, alias) && source[alias] !== null && source[alias] !== undefined) {
        return { known: true, value: isSelectedTax(source[alias]) };
      }
    }
  }
  return { known: false, value: false };
}
const cleanNamePart = value => {
  const text = String(value == null ? '' : value).trim();
  return ['null', 'undefined'].includes(text.toLowerCase()) ? '' : text;
};

export default {
  name: 'ModalBillExtraWithoutContractComponent',

  data() {
    return {
      catalogue: new Catalogue(),
      source: {},
      contextReady: false,
      contextError: '',
      formError: '',
      load: false,
      send: false,
      billingDataOptions: [],
      bills: [],
      billsData: [],
      nextBillKey: 1,
      differenceConfirmed: false,
      requestSequence: 0,
      taxOptions: [
        { key: 'iva', label: 'IVA 16%' },
        { key: 'ieps', label: 'IEPS 8%' },
        { key: 'isr_ret', label: 'ISR retenido 10%' },
        { key: 'iva_ret', label: 'IVA retenido 10.6667%' },
      ],
    };
  },

  computed: {
    invoiceCurrencies() {
      const source = this.catalogue.currencies || [];
      return source.length
        ? source.filter(currency => [1, 2].includes(Number(currency.id)))
        : [{ id: 1, name: 'MXN' }, { id: 2, name: 'USD' }];
    },
    sourceCurrencyId() {
      const value = this.source.currency_id != null
        ? this.source.currency_id
        : this.source.source_currency_id;
      return VALID_CURRENCIES.includes(Number(value)) ? Number(value) : null;
    },
    userName() {
      const user = this.source.user || {};
      return [user.name || user.user_nombre, user.last_name || user.user_apellido]
        .map(cleanNamePart).filter(Boolean).join(' ') || 'Cliente sin identificar';
    },
    companyName() {
      return (this.source.company || {}).name || 'Empresa sin identificar';
    },
    isPaid() { return this.source.is_paid === true || Number(this.source.is_paid) === 1; },
    // Todos los conceptos pendientes forman parte de la factura;
    // no se pide seleccionarlos nuevamente en este modal.
    selectedItems() { return this.bills; },
    originalSelectedTotal() {
      return moneyRound(this.selectedItems.reduce((sum, item) => sum + numberOrZero(item.original_final_amount), 0));
    },
    selectedTotal() {
      return moneyRound(this.selectedItems.reduce((sum, item) => sum + this.calcularTotales(item).total, 0));
    },
    differenceAmount() { return moneyRound(Math.abs(this.selectedTotal - this.originalSelectedTotal)); },
    validationError() { return this.validationMessage(); },
    canSubmit() { return this.contextReady && !this.validationError; },
  },

  mounted() {
    if (typeof this.catalogue.getCurrencies === 'function') {
      this.catalogue.getCurrencies();
    }
  },
  created() {
    Event.$on('event-show-bill-extra-without-contract', this.openModal);
  },
  beforeDestroy() {
    Event.$off('event-show-bill-extra-without-contract', this.openModal);
  },

  methods: {
    ...mapActions({
      errorNotification: 'alert/error',
      successNotification: 'alert/success',
    }),

    currencyLabel(id) {
      if (Number(id) === 1) return 'MXN';
      if (Number(id) === 2) return 'USD';
      return 'Sin definir';
    },
    money(value, currencyId) {
      if (!VALID_CURRENCIES.includes(Number(currencyId))) {
        return `${numberOrZero(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (sin moneda)`;
      }
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: this.currencyLabel(currencyId),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numberOrZero(value));
    },
    formatDate(value) {
      return value ? String(value).substring(0, 10) : '';
    },
    normalizedArray(value) {
      if (Array.isArray(value)) return value;
      return value && typeof value === 'object' ? [value] : [];
    },
    reset() {
      this.requestSequence += 1;
      this.source = {};
      this.contextReady = false;
      this.contextError = '';
      this.formError = '';
      this.load = false;
      this.send = false;
      this.billingDataOptions = [];
      this.bills = [];
      this.billsData = [];
      this.nextBillKey = 1;
      this.differenceConfirmed = false;
    },
    closedModal() {
      if (this.send) return;
      this.$modal.hide('gm-bill-extra-without-contract');
      this.reset();
    },

    async openModal(buy) {
      this.reset();
      if (!buy || !Number.isInteger(Number(buy.id)) || Number(buy.id) <= 0) return;
      if (buy.source_type === 'contract' || buy.has_contract === true || buy.contract_id) return;
      this.source = Object.assign({}, buy);
      this.$modal.show('gm-bill-extra-without-contract');
      this.load = true;
      const currentRequest = this.requestSequence;

      try {
        // Devuelve el cargo actualizado con moneda original, receptor,
        // conceptos e información de cuáles ya tienen CFDI vigente.
        const response = await request.get(`/extra_dues/${buy.id}/invoice-context`);
        if (currentRequest !== this.requestSequence) return;
        const root = response && response.data ? response.data : {};
        const context = root.data && typeof root.data === 'object' ? root.data : root;
        const extraDue = context.extra_due || context.extraDue || context;
        if (!extraDue || Number(extraDue.id) !== Number(buy.id)) {
          throw new Error('El contexto fiscal no corresponde al cargo seleccionado.');
        }
        this.source = Object.assign({}, buy, extraDue);
        // currency_id debe estar guardado en extra_dues y regresar en invoice-context.
        // El listado anterior de /contract_dues no lo incluía para los extras independientes.
        if (!VALID_CURRENCIES.includes(Number(extraDue.currency_id))) {
          throw new Error('GET invoice-context debe devolver extra_due.currency_id guardado (1=MXN, 2=USD).');
        }
        const rawItems = extraDue.due_items_extras || extraDue.items || [];
        this.setItems(rawItems);
        this.setBillingData(context, extraDue);
        await this.loadCustomerBillingData(extraDue);
        if (currentRequest !== this.requestSequence) return;
        this.addBillData();
        this.contextReady = true;
      } catch (error) {
        if (currentRequest !== this.requestSequence) return;
        this.contextError = `No se pudo preparar la facturación: ${this.extractError(error)}`;
        this.contextReady = false;
      } finally {
        if (currentRequest === this.requestSequence) this.load = false;
      }
    },

    setItems(rawItems) {
      this.bills = this.normalizedArray(rawItems)
        .filter(item => item && item.id && !item.item_bill && !isSelectedTax(item.is_billed))
        .map(item => {
          const base = numberOrZero(item.base_amount);
          const finalAmount = numberOrZero(item.final_amount);
          const isNegative = isSelectedTax(item.negative);
          const rules = TAX_KEYS.reduce((result, key) => {
            result[key] = storedTax(item, key);
            return result;
          }, {});
          return {
            id: Number(item.id),
            description: item.description || '',
            original_description: item.description || '',
            bill_id: 0,
            negative: isNegative,
            has_base_amount: item.base_amount !== undefined && item.base_amount !== null,
            base_amount: base,
            original_final_amount: finalAmount,
            // Importes registrados: base_amount = precio antes de impuesto,
            // final_amount = importe total del adeudo (no usarlo como base).
            quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1,
            unit_price: Number(item.unit_price) > 0 ? Number(item.unit_price) : base / (Number(item.quantity) > 0 ? Number(item.quantity) : 1),
            final_amount: base, // compatibilidad: importe unitario inicial sin impuestos
            discount: numberOrZero(item.discount),
            iva: rules.iva.known ? rules.iva.value : null,
            ieps: rules.ieps.known ? rules.ieps.value : null,
            isr_ret: rules.isr_ret.known ? rules.isr_ret.value : null,
            iva_ret: rules.iva_ret.known ? rules.iva_ret.value : null,
            product_key: String(item.product_key || ''),
            unit_key: String(item.unit_key || '').toUpperCase(),
            unit_name: String(item.unit_name || ({ H87: 'Pieza', E48: 'Unidad de servicio' }[String(item.unit_key || '').toUpperCase()] || '')),
            // Si no viene objeto de impuesto, solo precargamos 02 cuando hay impuestos
            // seleccionados. Con todas las reglas en falso, el usuario debe elegirlo.
            taxability: String(item.taxability || (TAX_KEYS.some(key => rules[key].known && rules[key].value) ? '02' : '')),
          };
        });
    },

    setBillingData(context, extraDue) {
      // NO reutilizar companies_buildings: son datos de otro contrato/edificio.
      const candidates = context.billing_data_options
        || context.billing_data_list
        || extraDue.billing_data_options
        || (extraDue.user || {}).billing_data
        || context.billing_data
        || extraDue.billing_data;
      const userId = Number(extraDue.user_id || (extraDue.user || {}).id);
      this.billingDataOptions = this.normalizedArray(candidates).filter(item => {
        return item && item.id && (!item.user_id || Number(item.user_id) === userId);
      });
    },

    async loadCustomerBillingData(extraDue) {
      // Mismo helper de catálogos usado al crear el extra sin contrato.
      // No usar get_companies_buildings(): esa función carga receptor del edificio/contrato.
      const userId = Number(extraDue.user_id || (extraDue.user || {}).id);
      if (!userId || typeof this.catalogue.getBillingDataUser !== 'function') return;
      try {
        const response = await this.catalogue.getBillingDataUser(userId);
        const fromResponse = response && response.data !== undefined ? response.data : response;
        const values = Array.isArray(fromResponse) ? fromResponse
          : fromResponse && (fromResponse.data || fromResponse.billing_data || fromResponse.billing_data_options);
        const fromCatalogue = this.catalogue.billing_data_user
          || this.catalogue.billing_data_users
          || this.catalogue.billingDataUser
          || this.catalogue.billingDataUsers
          || this.catalogue.billing_data
          || [];
        const fetched = this.normalizedArray(values).length
          ? this.normalizedArray(values)
          : this.normalizedArray(fromCatalogue);
        const byId = new Map();
        // Solo se aceptan datos fiscales del cliente; no mezclar otro receptor.
        [...this.billingDataOptions, ...fetched].forEach(item => {
          if (item && item.id && (!item.user_id || Number(item.user_id) === userId)) {
            byId.set(Number(item.id), item);
          }
        });
        this.billingDataOptions = Array.from(byId.values());
      } catch (error) {
        // Los datos del endpoint invoice-context siguen siendo válidos
        // si el catálogo auxiliar no responde.
        if (!this.billingDataOptions.length) {
          throw new Error('No se pudieron cargar los datos fiscales del cliente: ' + this.extractError(error));
        }
      }
    },

    isUsdCharge() {
      return Number(this.sourceCurrencyId) === 2;
    },
    normalizeInvoiceCurrency(value) {
      // Misma regla del modal original: MXN siempre factura en MXN;
      // cuando el adeudo se registró en USD se permite USD o conversión a MXN.
      if (!this.isUsdCharge()) return 1;
      return Number(value) === 1 ? 1 : 2;
    },
    onInvoiceCurrencyChange(bill) {
      bill.currency_id = this.normalizeInvoiceCurrency(bill.currency_id);
      // No borrar el tipo de cambio en USD -> USD.
    },
    requiresCurrencyConversion(bill) {
      return this.isUsdCharge() && Number(bill && bill.currency_id) === 1;
    },
    hasValidExchangeRate(bill) {
      return !this.requiresExchange(bill) || numberOrZero(bill.exchange_rate_bill) > 0;
    },
    getConvertedAmount(item) {
      const bill = this.billsData[item.bill_id];
      const factor = bill ? this.conversionFactor(bill) : 1;
      return moneyRound(numberOrZero(item.quantity) * numberOrZero(item.unit_price) * factor);
    },
    getItemInvoiceCurrency(item) {
      const bill = this.billsData[item.bill_id];
      return this.currencyLabel(bill ? bill.currency_id : this.sourceCurrencyId);
    },
    billHasItems(index) {
      return this.bills.some(item => Number(item.bill_id) === Number(index));
    },
    invoiceTotalFormatted(index) {
      const bill = this.billsData[index];
      return this.money(this.invoiceTotals(index), bill && bill.currency_id);
    },
    newBill() {
      const preferred = Number(this.source.billing_data_id);
      const found = this.billingDataOptions.find(item => Number(item.id) === preferred);
      return {
        local_key: this.nextBillKey++,
        name: `Factura ${this.billsData.length + 1}`,
        billing_data_id: found ? preferred : (this.billingDataOptions.length === 1 ? Number(this.billingDataOptions[0].id) : null),
        type: 2,
        extra_due_id: Number(this.source.id),
        currency_id: this.normalizeInvoiceCurrency(this.sourceCurrencyId),
        exchange_rate_bill: 0,
        payment_method: 'PUE',
        payment_form: '',
      };
    },
    addBillData() {
      this.billsData.push(this.newBill());
    },
    removeBillData(index) {
      if (this.billsData.length <= 1) return;
      this.billsData.splice(index, 1);
      this.bills.forEach(item => {
        if (item.bill_id === index) item.bill_id = 0;
        else if (item.bill_id > index) item.bill_id -= 1;
      });
    },
    onPaymentMethodChange(bill) {
      if (bill.payment_method === 'PPD') bill.payment_form = '99';
      else if (bill.payment_form === '99') bill.payment_form = '';
    },
    invalidateConfirmation() { this.differenceConfirmed = false; },
    taxConfigurationComplete(item) {
      return TAX_KEYS.every(key => typeof item[key] === 'boolean');
    },
    toggleTax(item, key) {
      this.$set(item, key, item[key] === true ? false : true);
      // Solo sugerimos objeto 02 cuando se activa un impuesto;
      // jamás sobreescribimos una clasificación existente.
      if (item[key] && !item.taxability) this.$set(item, 'taxability', '02');
      this.invalidateConfirmation();
    },
    onUnitKeyChange(item) {
      const unit = String(item.unit_key || '').trim().toUpperCase();
      const names = { H87: 'Pieza', E48: 'Unidad de servicio' };
      if (names[unit] && !String(item.unit_name || '').trim()) {
        this.$set(item, 'unit_name', names[unit]);
      }
    },
    calcularTotales(item) {
      const bruto = numberOrZero(item.quantity) * numberOrZero(item.unit_price);
      const subtotal = Math.max(0, bruto - numberOrZero(item.discount));
      const ieps = item.ieps ? subtotal * 0.08 : 0;
      const baseImpuestos = subtotal + ieps;
      const iva = item.iva ? baseImpuestos * 0.16 : 0;
      const isrRet = item.isr_ret ? baseImpuestos * 0.10 : 0;
      const ivaRet = item.iva_ret ? baseImpuestos * 0.106667 : 0;
      return {
        subtotal: moneyRound(subtotal),
        ieps: moneyRound(ieps),
        iva: moneyRound(iva),
        isrRet: moneyRound(isrRet),
        ivaRet: moneyRound(ivaRet),
        taxNet: moneyRound(ieps + iva - isrRet - ivaRet),
        total: moneyRound(subtotal + ieps + iva - isrRet - ivaRet),
      };
    },
    itemMatchesCharge(item) {
      if (!this.taxConfigurationComplete(item)) return false;
      return Math.abs(this.calcularTotales(item).total - numberOrZero(item.original_final_amount)) <= 0.01;
    },
    taxAmount(item, key) {
      const amounts = this.calcularTotales(item);
      const field = { iva: 'iva', ieps: 'ieps', isr_ret: 'isrRet', iva_ret: 'ivaRet' }[key];
      return field ? amounts[field] : 0;
    },
    requiresExchange(invoice) {
      // Todo cargo USD requiere tipo de cambio, incluso CFDI USD.
      return this.isUsdCharge() && !!invoice;
    },
    conversionFactor(invoice) {
      // const target = Number(invoice.currency_id);
      const rate = numberOrZero(invoice.exchange_rate_bill);
      if (!this.requiresCurrencyConversion(invoice)) return 1;
      if (rate <= 0) return 0;
      return rate;
    },
    invoiceTotals(index) {
      const invoice = this.billsData[index];
      if (!invoice) return 0;
      const amount = this.selectedItems
        .filter(item => Number(item.bill_id) === index)
        .reduce((sum, item) => sum + this.calcularTotales(item).total, 0);
      return moneyRound(amount * this.conversionFactor(invoice));
    },

    validationMessage() {
      if (!this.contextReady) return 'No se ha cargado el contexto fiscal del cargo.';
      if (!Number(this.source.id) || !Number(this.source.user_id) || !Number(this.source.company_id)) {
        return 'El cargo debe tener cliente y empresa emisora válidos.';
      }
      if (!VALID_CURRENCIES.includes(Number(this.sourceCurrencyId))) {
        return 'El cargo no tiene moneda original válida. Corrígela en el backend antes de facturar.';
      }
      if (!this.bills.length) return 'No hay conceptos pendientes de facturar.';
      if (!this.billingDataOptions.length) return 'No hay datos fiscales del cliente.';

      for (const item of this.selectedItems) {
        if (!item.id || item.negative) return `El concepto ${item.description} no es facturable en este flujo.`;
        if (!String(item.description).trim()) return 'Existe un concepto sin descripción.';
        if (!item.has_base_amount) return `El contexto no devolvió base_amount de ${item.description}.`;
        if (!this.taxConfigurationComplete(item)) return `Define los cuatro impuestos del concepto ${item.description}.`;
        if (!(Number(item.quantity) > 0) || !(Number(item.unit_price) > 0)) return `Revisa cantidad y precio unitario de ${item.description}.`;
        if (numberOrZero(item.discount) < 0 || numberOrZero(item.discount) >= numberOrZero(item.quantity) * numberOrZero(item.unit_price)) {
          return `Revisa el descuento del concepto ${item.description}.`;
        }
        if (!Number.isInteger(Number(item.bill_id)) || !this.billsData[item.bill_id]) {
          return 'Todos los conceptos seleccionados deben estar asignados a una factura.';
        }
        if (!/^\d{8}$/.test(item.product_key)) return `Captura la clave SAT de 8 dígitos para ${item.description}.`;
        if (!/^[A-Z0-9]{2,3}$/i.test(item.unit_key)) return `Captura la unidad SAT de ${item.description}.`;
        if (!String(item.unit_name || '').trim()) return `Captura el nombre de unidad de ${item.description}.`;
        if (!['01','02','03','04'].includes(item.taxability)) return `Selecciona el objeto de impuesto para ${item.description}.`;
        if (item.taxability !== '02' && TAX_KEYS.some(key => item[key])) return `El objeto de impuesto de ${item.description} es incompatible con sus impuestos.`;
      }
      for (let index = 0; index < this.billsData.length; index += 1) {
        const invoice = this.billsData[index];
        const items = this.selectedItems.filter(item => Number(item.bill_id) === index);
        if (!items.length) continue;
        if (!this.billingDataOptions.some(option => Number(option.id) === Number(invoice.billing_data_id))) {
          return `Selecciona los datos fiscales de la factura ${index + 1}.`;
        }
        if (!VALID_CURRENCIES.includes(Number(invoice.currency_id)) || Number(invoice.currency_id) !== this.normalizeInvoiceCurrency(invoice.currency_id)) {
          return `Moneda inválida en la factura ${index + 1}.`;
        }
        if (this.requiresExchange(invoice) && !(Number(invoice.exchange_rate_bill) > 0)) {
          return `Indica el tipo de cambio de la factura ${index + 1}.`;
        }
        if (!['PUE', 'PPD'].includes(invoice.payment_method)) return `Método de pago inválido en la factura ${index + 1}.`;
        if (invoice.payment_method === 'PUE' && (!invoice.payment_form || invoice.payment_form === '99')) {
          return `Selecciona la forma de pago de la factura ${index + 1}.`;
        }
        if (Number(invoice.type) === 1) {
          const first = items[0];
          const incompatible = items.some(item => (
            item.product_key !== first.product_key ||
            item.unit_key !== first.unit_key ||
            item.unit_name !== first.unit_name ||
            item.taxability !== first.taxability ||
            TAX_KEYS.some(key => Boolean(item[key]) !== Boolean(first[key]))
          ));
          if (incompatible) return `La factura ${index + 1} agrupa conceptos fiscales incompatibles. Usa desglosada.`;
        }
      }
      if (this.differenceAmount > 0.01 && !this.differenceConfirmed) {
        return 'Confirma la diferencia entre el CFDI y el adeudo antes de facturar.';
      }
      return '';
    },

    async triggerSave() {
      if (this.send) return;
      this.formError = this.validationMessage();
      if (this.formError) return;
      const usedGroups = this.billsData
        .map((invoice, index) => ({ ...invoice, original_index: index }))
        .filter(invoice => this.selectedItems.some(item => Number(item.bill_id) === invoice.original_index));
      const indexMap = {};
      usedGroups.forEach((invoice, index) => { indexMap[invoice.original_index] = index; });
      const items = this.selectedItems.map(item => ({
        id: item.id,
        extra_due_item_id: item.id,
        description: item.description,
        bill_id: indexMap[item.bill_id],
        quantity: numberOrZero(item.quantity),
        unit_price: numberOrZero(item.unit_price),
        final_amount: moneyRound(numberOrZero(item.quantity) * numberOrZero(item.unit_price)),
        original_final_amount: numberOrZero(item.original_final_amount),
        discount: numberOrZero(item.discount),
        iva: Boolean(item.iva),
        ieps: Boolean(item.ieps),
        isr_ret: Boolean(item.isr_ret),
        iva_ret: Boolean(item.iva_ret),
        product_key: item.product_key,
        unit_key: item.unit_key.toUpperCase(),
        unit_name: item.unit_name,
        taxability: item.taxability,
      }));
      const billsData = usedGroups.map(invoice => ({
        name: invoice.name,
        extra_due_id: Number(this.source.id),
        billing_data_id: Number(invoice.billing_data_id),
        type: Number(invoice.type),
        currency_id: Number(invoice.currency_id),
        exchange_rate_bill: this.requiresExchange(invoice) ? numberOrZero(invoice.exchange_rate_bill) : 0,
        payment_method: invoice.payment_method,
        payment_form: invoice.payment_method === 'PPD' ? '99' : invoice.payment_form,
      }));
      const form = new FormData();
      form.append('source_type', 'without_contract');
      form.append('extra_due_id', String(this.source.id));
      form.append('difference_confirmed', this.differenceAmount > 0.01 && this.differenceConfirmed ? '1' : '0');
      form.append('bills', JSON.stringify(items));
      form.append('bills_data', JSON.stringify(billsData));

      this.send = true;
      try {
        const { data } = await request.post(`/extra_dues/${this.source.id}/invoice`, form);
        this.successNotification((data && data.message) || 'Factura emitida correctamente.');
        this.$modal.hide('gm-bill-extra-without-contract');
        this.reset();
        Event.$emit('event-load-contract_dues');
        Event.$emit('event-load-extra-dues-without-contract');
      } catch (error) {
        this.formError = this.extractError(error);
        if (typeof this.errorNotification === 'function') await this.errorNotification(error);
      } finally {
        this.send = false;
      }
    },
    extractError(error) {
      const response = error && error.response && error.response.data;
      const message = response && (response.message || response.errors);
      const base = typeof message === 'string' ? message : (error && error.message) || 'Ocurrió un error al generar la factura.';
      const prefix = 'Unable to create Invoice: ';
      if (base.includes(prefix)) {
        try {
          const parsed = JSON.parse(base.substring(base.indexOf(prefix) + prefix.length));
          return parsed.message || base;
        } catch (_) {
          return base;
        }
      }
      return base;
    },
  },
};
</script>

<style scoped>
.billing-modal {
    --primary: #5672a5;
    --primary-dark: #304d7d;
    --primary-soft: #eef3fb;
    --success: #269b74;
    --success-soft: #eaf8f3;
    --warning: #d99b24;
    --warning-soft: #fff8e8;
    --danger: #d84d5d;
    --danger-soft: #fff0f2;
    --border: #dfe5ee;
    --surface: #ffffff;
    --background: #f5f7fb;
    --text: #1f2937;
    --muted: #6b7280;

    background: var(--background);
    color: var(--text);
    min-height: 400px;
}

/* HEADER */
.billing-modal__header {
    align-items: center;
    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
    color: #ffffff;
    display: flex;
    gap: 18px;
    justify-content: space-between;
    padding: 18px 22px;
}

.billing-modal__title-group {
    align-items: center;
    display: flex;
    gap: 14px;
    min-width: 0;
}

.billing-modal__icon {
    align-items: center;
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    flex: 0 0 46px;
    font-size: 22px;
    height: 46px;
    justify-content: center;
    width: 46px;
}

.billing-modal__title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 700;
    margin: 0;
}

.billing-modal__subtitle {
    color: rgba(255, 255, 255, 0.78);
    font-size: 12px;
    line-height: 1.45;
    margin: 3px 0 0;
}

.billing-modal__close {
    align-items: center;
    background: rgba(255, 255, 255, 0.12);
    border: 0;
    border-radius: 50%;
    color: #ffffff;
    cursor: pointer;
    display: flex;
    flex: 0 0 36px;
    height: 36px;
    justify-content: center;
    transition: 0.2s ease;
    width: 36px;
}

.billing-modal__close:hover {
    background: rgba(255, 255, 255, 0.24);
    color: #ffffff;
    transform: rotate(90deg);
}

/* BODY */
.billing-modal__body {
    max-height: calc(100vh - 185px);
    overflow-y: auto;
    padding: 22px;
}

.billing-section-header {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 14px;
}

.billing-section-header__left {
    align-items: flex-start;
    display: flex;
    gap: 12px;
    min-width: 0;
}

.billing-section-header__number {
    align-items: center;
    background: var(--primary-soft);
    border-radius: 50%;
    color: var(--primary-dark);
    display: flex;
    flex: 0 0 30px;
    font-size: 13px;
    font-weight: 700;
    height: 30px;
    justify-content: center;
    width: 30px;
}

.billing-section-header__title {
    color: var(--text);
    font-size: 15px;
    font-weight: 700;
    margin: 1px 0 3px;
}

.billing-section-header__text {
    color: var(--muted);
    font-size: 12px;
    line-height: 1.4;
    margin: 0;
}

.billing-count-badge {
    align-items: center;
    background: var(--primary-soft);
    border: 1px solid #dbe5f3;
    border-radius: 999px;
    color: var(--primary-dark);
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 700;
    justify-content: center;
    min-width: 29px;
    padding: 5px 9px;
}

/* FACTURAS Y CONCEPTOS */
.invoice-list {
    display: grid;
    gap: 12px;
    margin-bottom: 12px;
}

.invoice-card,
.item-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}

.invoice-card__header,
.item-card__header {
    align-items: center;
    background: #fafbfd;
    border-bottom: 1px solid var(--border);
    display: flex;
    gap: 11px;
    justify-content: space-between;
    padding: 13px 15px;
}

.invoice-card__header {
    justify-content: flex-start;
}

.invoice-card__number,
.item-card__index {
    align-items: center;
    background: var(--primary-soft);
    border-radius: 8px;
    color: var(--primary-dark);
    display: flex;
    flex: 0 0 32px;
    font-size: 12px;
    font-weight: 700;
    height: 32px;
    justify-content: center;
    width: 32px;
}

.invoice-card__heading,
.item-card__title > div {
    min-width: 0;
}

.invoice-card__heading strong,
.invoice-card__heading small {
    display: block;
}

.invoice-card__heading strong {
    color: var(--text);
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.invoice-card__heading small {
    color: var(--muted);
    font-size: 10px;
    margin-top: 2px;
}

.invoice-card__body,
.item-card__body {
    padding: 16px;
}

.invoice-card__body .form-group,
.item-card__body .form-group {
    margin-bottom: 14px;
}

.invoice-card__body .form-group:last-child,
.item-card__body .form-group:last-child {
    margin-bottom: 0;
}

.item-card {
    margin-bottom: 14px;
}

.item-card__title {
    align-items: center;
    display: flex;
    gap: 10px;
    min-width: 0;
}

.item-card__label {
    color: var(--muted);
    display: block;
    font-size: 10px;
    margin-bottom: 2px;
    text-transform: uppercase;
}

.item-card__title strong {
    color: var(--text);
    display: block;
    font-size: 12px;
    line-height: 1.35;
}

.item-card__amount {
    flex: 0 0 auto;
    text-align: right;
}

.item-card__amount span,
.item-card__amount strong,
.item-card__amount small {
    display: block;
}

.item-card__amount span {
    color: var(--muted);
    font-size: 10px;
}

.item-card__amount strong {
    color: var(--primary-dark);
    font-size: 14px;
    margin-top: 2px;
}

.billing-conversion {
    color: var(--success);
    font-size: 10px;
    font-weight: 700;
    margin-top: 3px;
}

/* CAMPOS */
.billing-label {
    color: #374151;
    display: block;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
}

.billing-control.form-control,
.form-control.billing-control {
    border-color: #d5dce6;
    border-radius: 7px;
    font-size: 12px;
    min-height: 38px;
}

.billing-control.form-control:focus,
.form-control.billing-control:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(86, 114, 165, 0.12);
}

.billing-help {
    color: var(--muted);
    display: block;
    font-size: 10px;
    line-height: 1.45;
    margin-top: 5px;
}

.billing-error {
    color: var(--danger);
    font-size: 11px;
    margin-top: 5px;
}

.billing-readonly-value {
    align-items: center;
    background: #f8fafc;
    border: 1px solid #d5dce6;
    border-radius: 7px;
    color: var(--text);
    display: flex;
    font-size: 12px;
    font-weight: 700;
    min-height: 38px;
    padding: 7px 10px;
}

.billing-readonly-value--currency {
    align-items: flex-start;
    flex-direction: column;
    justify-content: center;
}

.billing-readonly-value--currency small {
    color: var(--success);
    display: block;
    font-size: 10px;
    font-weight: 700;
    margin-top: 2px;
}

/* TIPO DE CAMBIO */
.exchange-panel {
    background: var(--warning-soft);
    border: 1px solid #f1d89d;
    border-radius: 9px;
    padding: 12px;
}

.exchange-panel__title {
    align-items: center;
    color: #8c6515;
    display: flex;
    font-size: 11px;
    font-weight: 700;
    gap: 7px;
    margin-bottom: 10px;
}

.conversion-preview {
    align-items: center;
    background: var(--success-soft);
    border: 1px solid #ccebdd;
    border-radius: 10px;
    display: flex;
    gap: 11px;
    margin: 2px 0 14px;
    padding: 11px 13px;
}

.conversion-preview__icon {
    align-items: center;
    background: #ffffff;
    border-radius: 8px;
    color: var(--success);
    display: flex;
    flex: 0 0 34px;
    height: 34px;
    justify-content: center;
    width: 34px;
}

.conversion-preview__content strong,
.conversion-preview__content span {
    display: block;
}

.conversion-preview__content strong {
    color: #1f6f55;
    font-size: 11px;
    margin-bottom: 2px;
}

.conversion-preview__content span {
    color: #3f5f54;
    font-size: 11px;
}

.conversion-preview__pending {
    color: #8c6515 !important;
}

/* IMPUESTOS */
.tax-panel {
    background: #f8fafc;
    border: 1px solid var(--border);
    border-radius: 10px;
    margin-top: 15px;
    padding: 14px;
}

.tax-panel__header {
    margin-bottom: 11px;
}

.tax-panel__header strong,
.tax-panel__header small {
    display: block;
}

.tax-panel__header strong {
    color: var(--text);
    font-size: 12px;
}

.tax-panel__header small {
    color: var(--muted);
    font-size: 10px;
    margin-top: 2px;
}

.tax-options {
    display: grid;
    gap: 9px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
}

.tax-option {
    align-items: center;
    background: #ffffff;
    border: 1px solid var(--border);
    border-radius: 9px;
    cursor: pointer;
    display: flex;
    gap: 9px;
    margin: 0;
    min-height: 56px;
    padding: 10px;
    position: relative;
    transition: 0.2s ease;
}

.tax-option:hover {
    border-color: var(--primary);
    box-shadow: 0 4px 12px rgba(48, 77, 125, 0.08);
    transform: translateY(-1px);
}

.tax-option--active {
    background: var(--primary-soft);
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(86, 114, 165, 0.08);
}

.tax-option input {
    height: 1px;
    opacity: 0;
    position: absolute;
    width: 1px;
}

.tax-option__check {
    align-items: center;
    background: #f3f5f8;
    border-radius: 7px;
    color: transparent;
    display: flex;
    flex: 0 0 30px;
    font-size: 11px;
    height: 30px;
    justify-content: center;
    transition: 0.2s ease;
    width: 30px;
}

.tax-option--active .tax-option__check {
    background: #ffffff;
    color: var(--primary);
}

.tax-option strong,
.tax-option small {
    display: block;
}

.tax-option strong {
    color: var(--text);
    font-size: 11px;
}

.tax-option small {
    color: var(--muted);
    font-size: 9px;
    margin-top: 1px;
}

/* RESUMEN */
.item-summary {
    border: 1px solid var(--border);
    border-radius: 10px;
    margin-top: 14px;
    overflow: hidden;
}

.item-summary__title {
    align-items: center;
    background: var(--primary-dark);
    color: #ffffff;
    display: flex;
    font-size: 11px;
    font-weight: 700;
    padding: 10px 13px;
}

.item-summary__currency {
    background: rgba(255, 255, 255, 0.14);
    border-radius: 999px;
    font-size: 9px;
    margin-left: auto;
    padding: 3px 7px;
}

.item-summary__warning {
    background: var(--warning-soft);
    color: #8c6515;
    font-size: 11px;
    padding: 12px 13px;
}

.item-summary__row,
.item-summary__total {
    align-items: center;
    border-bottom: 1px solid #edf0f4;
    display: flex;
    font-size: 11px;
    justify-content: space-between;
    padding: 9px 13px;
}

.item-summary__row span {
    color: var(--muted);
}

.item-summary__row strong {
    color: var(--text);
    font-size: 11px;
    text-align: right;
}

.item-summary__row--discount,
.item-summary__row--retention {
    background: #fffafb;
}

.item-summary__row--discount strong,
.item-summary__row--retention strong {
    color: var(--danger);
}

.item-summary__row--subtotal {
    background: #fafbfd;
}

.item-summary__total {
    background: var(--primary-soft);
    border-bottom: 0;
    color: var(--primary-dark);
    padding: 11px 13px;
}

.item-summary__total span {
    font-weight: 600;
}

.item-summary__total strong {
    color: var(--primary-dark);
    font-size: 14px;
    text-align: right;
}

/* BOTONES */
.billing-btn {
    border-radius: 7px;
    font-size: 12px;
    font-weight: 600;
    min-height: 38px;
    padding: 8px 14px;
}

.billing-btn--add,
.billing-btn--add-item {
    background: var(--primary-soft);
    border: 1px solid #d8e2f1;
    color: var(--primary-dark);
}

.billing-btn--add:hover,
.billing-btn--add-item:hover {
    background: var(--primary);
    border-color: var(--primary);
    color: #ffffff;
}

.billing-btn--add-item {
    width: 100%;
}

.billing-btn--secondary {
    background: #ffffff;
    border: 1px solid var(--border);
    color: #4b5563;
    min-width: 120px;
}

.billing-btn--secondary:hover {
    background: #f3f5f8;
    color: var(--text);
}

.billing-btn--primary {
    background: var(--success);
    border: 1px solid var(--success);
    color: #ffffff;
    min-width: 160px;
}

.billing-btn--primary:hover,
.billing-btn--primary:focus {
    background: #218562;
    border-color: #218562;
    color: #ffffff;
}

.billing-btn--primary:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

/* ESTADOS */
.billing-loading,
.billing-empty-state {
    align-items: center;
    color: var(--muted);
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 220px;
    padding: 35px 20px;
    text-align: center;
}

.billing-loading {
    background: var(--background);
}

.billing-loading__spinner,
.billing-empty-state__icon {
    align-items: center;
    background: var(--primary-soft);
    border-radius: 50%;
    color: var(--primary);
    display: flex;
    font-size: 22px;
    height: 48px;
    justify-content: center;
    margin-bottom: 11px;
    width: 48px;
}

.billing-loading h5,
.billing-empty-state h5 {
    color: var(--text);
    font-size: 14px;
    font-weight: 700;
}

.billing-loading p,
.billing-empty-state p {
    font-size: 11px;
}

/* FOOTER */
.billing-modal__footer {
    align-items: center;
    background: #ffffff;
    border-top: 1px solid var(--border);
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 14px 22px;
}

.billing-modal__footer-info {
    align-items: center;
    color: var(--muted);
    display: flex;
    font-size: 10px;
    line-height: 1.4;
}

.billing-modal__footer-info i {
    color: var(--primary);
}

.billing-modal__actions {
    display: flex;
    gap: 10px;
}

@media (max-width: 1200px) {
    .tax-options {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 991px) {
    .billing-modal__body {
        padding: 16px;
    }

    .billing-section-header--items {
        margin-top: 2px;
    }
}

@media (max-width: 768px) {
    .billing-modal__header {
        padding: 15px;
    }

    .billing-modal__subtitle {
        display: none;
    }

    .billing-modal__body {
        padding: 14px;
    }

    .tax-options {
        grid-template-columns: 1fr;
    }

    .item-card__header,
    .billing-modal__footer {
        align-items: stretch;
        flex-direction: column;
    }

    .item-card__amount {
        margin-left: 42px;
        text-align: left;
    }

    .billing-modal__footer-info {
        display: none;
    }

    .billing-modal__actions {
        flex-direction: column-reverse;
        width: 100%;
    }

    .billing-modal__actions .billing-btn {
        width: 100%;
    }
}


/* Adaptación exclusiva del nuevo modal; el modal con contrato no se modifica. */
.billing-modal--without-contract .billing-modal__source { display:flex; justify-content:space-between; align-items:center; gap:14px; background:#eef3fb; border-bottom:1px solid #dfe5ee; padding:13px 22px; }
.billing-modal--without-contract .billing-modal__source-data { min-width:0; }
.billing-modal--without-contract .billing-modal__source-data > strong, .billing-modal--without-contract .billing-modal__source-data > small { display:block; }
.billing-modal--without-contract .billing-modal__source-data > strong { font-size:14px; color:#304d7d; margin:4px 0; }
.billing-modal--without-contract .billing-modal__source-data > small { color:#6b7280; font-size:11px; }
.billing-modal--without-contract .billing-modal__source-label { display:block; color:#657e9b; font-size:10px; font-weight:700; letter-spacing:.06em; }
.billing-modal--without-contract .billing-modal__source-meta { display:flex; flex-direction:column; align-items:flex-end; gap:3px; font-size:11px; color:#5c6e85; }
.billing-modal--without-contract .billing-modal__source-badge { display:inline-block; background:#dff3e9; color:#1c7650; padding:3px 9px; border-radius:999px; font-weight:700; }
.billing-modal--without-contract .billing-modal__message { font-size:11px; line-height:1.5; padding:12px 14px; border-radius:8px; margin:12px 22px 0; }
.billing-modal--without-contract .billing-modal__message--warning { background:#fff8e8; border:1px solid #f1d89d; color:#8c6515; }
.billing-modal--without-contract .billing-modal__message--danger { background:#fff0f2; border:1px solid #f1c2c9; color:#b23949; }
.billing-modal--without-contract .billing-modal__message--nested { margin:8px 0 0; }
.billing-modal--without-contract .billing-modal__remove { margin-left:auto; background:transparent; border:none; color:#b64b58; cursor:pointer; }
.billing-modal--without-contract .billing-modal__remove:disabled { opacity:.4; cursor:not-allowed; }
.billing-modal--without-contract .billing-modal__help-warning { color:#b23949; font-size:10px; }
.billing-modal--without-contract .billing-readonly-value { flex-wrap:wrap; overflow-wrap:anywhere; }
.billing-modal--without-contract .billing-modal__taxes { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:8px; }
.billing-modal--without-contract .billing-modal__tax { cursor:pointer; text-align:left; display:flex; align-items:center; gap:7px; border:1px solid #dfe5ee; background:#fff; border-radius:9px; padding:9px; min-width:0; font-size:11px; }
.billing-modal--without-contract .billing-modal__tax--active { background:#eef3fb; border-color:#c0d2e8; }
.billing-modal--without-contract .billing-modal__tax--inactive { background:#f5f7fb; color:#8b93a0; }
.billing-modal--without-contract .billing-modal__tax--unknown { background:#fff8e8; border-color:#f1d89d; }
.billing-modal--without-contract .billing-modal__tax:focus-visible { outline:3px solid #5672a5; outline-offset:2px; }
.billing-modal--without-contract .billing-modal__tax:disabled { cursor:not-allowed; opacity:.6; }
.billing-modal--without-contract .billing-modal__difference-confirm { display:block; border-top:1px solid #f1d89d; margin-top:8px; padding-top:10px; }
.billing-modal--without-contract .billing-modal__difference-confirm p { margin-bottom:8px; }
.billing-modal--without-contract .billing-modal__tax-icon { display:grid; place-items:center; width:24px; height:24px; flex:0 0 24px; background:#f3f5f8; border-radius:6px; color:#8795a5; }
.billing-modal--without-contract .billing-modal__tax--active .billing-modal__tax-icon { background:#fff; color:#304d7d; }
.billing-modal--without-contract .billing-modal__tax strong, .billing-modal--without-contract .billing-modal__tax small { display:block; font-size:10px; }
.billing-modal--without-contract .billing-modal__tax small { color:#6b7280; margin-top:2px; }
.billing-modal--without-contract .billing-modal__invoice-total { display:flex; justify-content:space-between; gap:8px; margin-top:13px; padding-top:11px; border-top:1px dashed #dfe5ee; font-size:11px; }
.billing-modal--without-contract .billing-modal__invoice-total strong { color:#269b74; }
.billing-modal--without-contract .billing-modal__reconcile { background:#eaf8f3; border:1px solid #c8e9da; border-radius:10px; padding:13px 15px; font-size:11px; }
.billing-modal--without-contract .billing-modal__reconcile > strong { display:block; margin-bottom:6px; color:#1f6f55; }
.billing-modal--without-contract .billing-modal__reconcile > div { display:flex; justify-content:space-between; gap:10px; padding:3px 0; }
.billing-modal--without-contract .billing-modal__reconcile--error { background:#fff8e8; border-color:#f1d89d; color:#8c6515; }
.billing-modal--without-contract .billing-modal__reconcile--error > strong { color:#8c6515; }
.billing-modal--without-contract .billing-modal__footer-info { max-width:60%; }
@media(max-width:1200px) { .billing-modal--without-contract .billing-modal__taxes { grid-template-columns:repeat(2,minmax(0,1fr)); } }
@media(max-width:768px) {
 .billing-modal--without-contract .billing-modal__source { align-items:flex-start; flex-direction:column; }
 .billing-modal--without-contract .billing-modal__source-meta { align-items:flex-start; }
 .billing-modal--without-contract .billing-modal__taxes { grid-template-columns:1fr; }
 .billing-modal--without-contract .billing-modal__footer-info { max-width:100%; }
}

</style>
